// components/ActivePaymentAgreementCard.tsx
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { XCircle } from "lucide-react";

import { PaymentInstallment } from "../../types";

interface ActivePaymentAgreementCardProps {
  agreementId: string;
  startDate: string;
  frequency: string;
  totalAmount: number;
  downPayment: number;
  installmentAmount: number;
  remainingAmount: number;
  paidInstallments: number;
  totalInstallments: number;
  installments: any[];
  showActions?: boolean; // Control whether to show action column
  latestBillAmount?: number;
  excludeLatestBill?: boolean;
  totalOutstanding?: string;
  onCancelAgreement?: () => void; // Optional cancel handler
}

export function ActivePaymentAgreementCard({
  agreementId,
  startDate,
  frequency,
  totalAmount,
  downPayment,
  installmentAmount,
  remainingAmount,
  paidInstallments,
  totalInstallments,
  installments,
  showActions = true,
  latestBillAmount = 0,
  excludeLatestBill = false,
  totalOutstanding,
  onCancelAgreement,
}: ActivePaymentAgreementCardProps) {
  const currency = useCurrency();
  const navigate = useNavigate();
  const progressPercentage = (paidInstallments / totalInstallments) * 100;
  const params = useParams();
  const [confirmCancel, setConfirmCancel] = useState(false);

  // GenericTable columns configuration
  const columns: TableColumn<PaymentInstallment>[] = [
    {
      key: "installmentNo",
      header: "Installment #",
      type: "text",
    },
    {
      key: "paymentDate",
      header: "Due Date",
      type: "text",
    },
    {
      key: "paidAmount",
      header: "Amount",
      type: "custom",
      render: (value: number) => (
        <span className="text-sm text-gray-900">
          {currency} {value}
        </span>
      ),
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          switch (value) {
            case "Paid":
              return "default" as const;
            case "Pending":
              return "secondary" as const;
            case "Overdue":
              return "destructive" as const;
            default:
              return "outline" as const;
          }
        },
        getClassName: (value: string) => {
          switch (value) {
            case "Paid":
              return "bg-green-100 text-green-800";
            case "Pending":
              return "bg-yellow-100 text-yellow-800";
            case "Overdue":
              return "bg-red-100 text-red-800";
            default:
              return "bg-gray-100 text-gray-800";
          }
        },
      },
    },
    {
      key: "paidDate",
      header: "Paid Date",
      type: "custom",
      render: (value?: string) => (
        <span className="text-sm text-gray-900">{value || "NA"}</span>
      ),
    },
  ];

  const leftItems = [
    {
      label: "Total Outstanding",
      value: `${currency} ${totalOutstanding}`,
    },
    excludeLatestBill && {
      label: "Less: Latest Bill",
      value: `- ${currency} ${latestBillAmount}`,
      valueClass: "font-medium text-red-600",
    },
    {
      label: "Less: Down Payment",
      value: `- ${currency} ${downPayment}`,
      valueClass: "font-medium text-red-600",
    },
  ].filter(Boolean);

  const rightItems = [
    {
      label: "Installment Amount",
      value: `${currency} ${installmentAmount}`,
      valueClass: "font-bold text-green-700",
    },
    {
      label: "Agreement End Date",
      value:
        installments.length > 0
          ? installments[installments.length - 1].dueDate
          : "NA",
    },
    {
      label: "Remaining Balance",
      value: `${currency} ${remainingAmount}`,
      valueClass: "font-semibold text-orange-600",
    },
  ];

  // GenericTable actions configuration
  const actions: TableAction[] = useMemo(() => {
    return [
      {
        label: "Pay",
        onClick: (row: any) => {
          navigate("/cx/payments/create", {
            state: {
              consumerNumber: params.consumerNumber,
              paymentType: "6",
              installmentId: row.id,
            },
          });
        },
        icon: "DollarSign",
        // hidden: (row: Installment) =>
        //   row.status !== "Pending" && row.status !== "Overdue",
      },
    ];
  }, [showActions]);

  return (
    <Card className="border-l-4 border-l-blue-500">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Active Payment Agreement
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Agreement ID: {agreementId}
            </p>
          </div>
          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
            Active
          </Badge>
        </div>

        {/* Agreement Summary Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6">
          <h4 className="font-semibold text-blue-900 text-sm mb-4">
            Agreement Summary
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3">
            {/* Left */}
            <div className="space-y-3">
              {leftItems.map((item, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center text-sm"
                >
                  <span className="text-gray-600">{item.label}</span>
                  <span
                    className={item.valueClass ?? "font-medium text-gray-900"}
                  >
                    {item.value}
                  </span>
                </div>
              ))}

              {/* Agreement Amount (kept separate due to border & emphasis) */}
              <div className="flex justify-between items-center text-sm border-t border-blue-100 pt-2">
                <span className="font-semibold text-blue-900">
                  Agreement Amount
                </span>
                <span className="font-bold text-blue-700">
                  {currency} {totalAmount}
                </span>
              </div>
            </div>

            {/* Right */}
            <div className="space-y-3">
              {rightItems.map((item, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center text-sm"
                >
                  <span className="text-gray-600">{item.label}</span>
                  <span
                    className={item.valueClass ?? "font-medium text-gray-900"}
                  >
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">
              Installment Progress
            </p>
            <p className="text-sm text-gray-600">
              {paidInstallments} of {totalInstallments} paid
            </p>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1 text-right">
            {progressPercentage}% Complete
          </p>
        </div>

          {/* Installments Table - Using GenericTable */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            Installment Schedule
          </h4>
          <GenericTable
            data={installments ?? []}
            columns={columns}
            rowKey={(row) => `installment-${row.installmentNo}`}
            actions={actions}
            actionsType="buttons"
            emptyMessage="No installments found"
          />
        </div>

        {/* Cancel Agreement */}
        {onCancelAgreement && (
          <div className="flex justify-end mt-4 pt-4 border-t border-gray-100">
            {confirmCancel ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">
                  Are you sure you want to cancel this agreement?
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmCancel(false)}
                >
                  No, Keep it
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setConfirmCancel(false);
                    onCancelAgreement();
                  }}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Yes, Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setConfirmCancel(true)}
              >
                <XCircle className="h-4 w-4 mr-1" />
                Cancel Agreement
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
